/**
 * プロジェクトの名前を後から変える（2026-09-28 creator/project-rename・AGT-10）の、画面から切り離した判断（node --test で確かめる・projectRename.test.ts）。
 * 画面は components/ProjectNameCard.tsx（ホスティングの画面の「プロジェクト名」のカード・URL スラッグのカードの直前）。
 *
 * API の正: Core の apps/server/src/modules/platform/projectRename.ts（core/project-rename bb785e2c・Creator と合意した形）。
 *   PATCH /v1/projects/:projectId { name }
 *     → 200 { project: { id, name, slug, createdAt }, previousName, changed }
 *       changed:false = サーバーで掃除した後の名前が今と同じ（べき等。通知メールも監査も無し）
 *   エラー: 400 invalid_request（掃除して空・80 文字超）/ 400 name_reserved（"Kumo" を単語として含む・運営の言葉だけ = details.name）/
 *           404 / 409 account_closed / 429 rate_limited（開発者ごと 1 時間 20 回・rateLimitPolicy.ts の platform.projectRename.developer）
 *
 * WHY 名前を変えられるようにするか: 名前はプレイヤーに届くメールの件名と差出人の表示名に出る。作った後に変える口が無く、
 *   仮の名前（"test" 等）のまま利用者にメールが届いていた（Quality のエージェントの試しで発見）。
 *
 * import は型だけ（ランタイムの import を持たない = node --experimental-strip-types でそのまま読める。slugRename.ts と同じ決め事）。
 */
import type { MessageKey } from './i18n';

/**
 * 名前の上限（サーバーの PROJECT_NAME_MAX と同じ 80・作成の画面の maxLength={80} とも同じ）。
 * 数え方も同じ String.length（UTF-16 の単位）。サーバーの zod も掃除も .length で見るので、画面だけ別の数え方にするとずれる
 */
export const PROJECT_NAME_MAX = 80;

export interface RenamedProject {
  id: string;
  name: string;
  slug: string;
  createdAt?: string;
}

/** PATCH /v1/projects/:projectId の応答 */
export interface ProjectRenameResult {
  project: RenamedProject;
  previousName: string;
  changed: boolean;
}

export const projectPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}`;

/**
 * サーバーの掃除（lib/displayName.ts の cleanUserLabel）の近似: NFC・制御文字と書式文字（幅 0・向きの印）→ 空白・空白を 1 つに・前後を落とす。
 * WHY 画面でも寄せるか: 「今と同じ名前か」をサーバーと同じ目で判断するため（"Sky  Racers " は保存しても changed:false になる =
 *   保存ボタンを押せても何も起きない、を避ける）。正はサーバー: ここで落とし損ねた文字があってもサーバーが掃除する（保存するのはサーバーの結果）
 */
export function normalizeProjectName(raw: string): string {
  return raw
    .normalize('NFC')
    .replace(/[\p{Cc}\p{Cf}\u2028\u2029]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * 入力の状態:
 *   'empty'     … 空（掃除して空も含む）= 保存できない
 *   'tooLong'   … 80 文字超 = 保存できない（黙って切り詰めない = 利用者の知らない名前にしない。入力欄にも maxLength を付けない = 貼り付けで黙って切れない）
 *   'unchanged' … 今の名前と同じ = 保存しても何も変わらない
 *   'ok'        … 保存できる
 * 長さは「前後を落とした入力」と「掃除した後」の大きい方で見る: サーバーは zod（trim だけ）で先に 80 を見てから掃除する =
 *   途中の空白の連続で 80 を超える入力は、掃除すると 80 以内でもサーバーが 400 を返す。画面で先に止める
 */
export type ProjectNameState = 'empty' | 'tooLong' | 'unchanged' | 'ok';

export function projectNameState(input: string, current: string): ProjectNameState {
  const cleaned = normalizeProjectName(input);
  if (!cleaned) return 'empty';
  if (Math.max(input.trim().length, cleaned.length) > PROJECT_NAME_MAX) return 'tooLong';
  if (cleaned === normalizeProjectName(current)) return 'unchanged';
  return 'ok';
}

/** 保存ボタンを押せるか（送信中は押せない = 二重に送らない） */
export const canSaveProjectName = (input: string, current: string, busy = false) => !busy && projectNameState(input, current) === 'ok';

/** 文字数の表示（"12/80"）に使う数。長さの判断（projectNameState）と同じ数を見せる = 「80 と出ているのに押せない」を起こさない */
export const projectNameLength = (input: string) => Math.max(input.trim().length, normalizeProjectName(input).length);

/** PATCH の本文。送るのは前後を落とした入力（中身の掃除はサーバーが正・ここで作り変えない） */
export const renameBody = (input: string) => ({ name: input.trim() });

export interface NoticeText {
  key: MessageKey;
  vars: Record<string, string>;
}

/** 保存できたときの知らせ。changed:false（べき等）は「もうその名前です」= 何も変わっていないことをはっきり言う */
export function renameNotice(res: ProjectRenameResult): NoticeText {
  if (!res.changed) return { key: 'projectName.unchanged', vars: { name: res.project.name } };
  return { key: 'projectName.renamed', vars: { old: res.previousName, new: res.project.name } };
}

/**
 * エラー → 文言のキー。null = 共通の errorText に任せる（not_found・network_error・route_missing 等は既存の訳がそのまま合う）。
 * WHY ここで上書きするか:
 *   - name_reserved: 「なぜ・どうすれば」を具体的に言う（error.name_reserved・作成の画面と同じ文言 = errorText も同じキーを引く）
 *   - rate_limited: 共通の文言は「N 秒後に」だけ。名前の変更は 1 時間 20 回の上限と分かる方が次の行動を決めやすい
 *   - account_closed: 共通の文言（en.account.ts）は「新しいゲームは作れません」= 名前の変更の画面では話がずれる
 *   - invalid_request: サーバーの英文（"Project name must be at most 80 characters"）ではなく、上限を訳した文で出す
 *   - それ以外のサーバーの失敗（internal_error・http_error 等）: 「変えられませんでした。少し待ってもう一度」の汎用の文
 */
export function renameErrorKey(err: { status?: number; code?: string } | null | undefined): MessageKey | null {
  if (!err) return null;
  if (err.code === 'name_reserved') return 'error.name_reserved';
  if (err.code === 'rate_limited' || err.status === 429) return 'projectName.error.rateLimited';
  if (err.code === 'account_closed') return 'projectName.error.accountClosed';
  if (err.code === 'invalid_request') return 'projectName.error.invalid';
  if (err.code === 'internal_error' || err.code === 'http_error' || (typeof err.status === 'number' && err.status >= 500)) return 'projectName.error.generic';
  return null;
}
